import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, ClipboardList } from 'lucide-react';
import { Badge, Button, EmptyState, toast } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useDre } from '../../../hooks/useFinancialOps';
import { financialOpsService } from '../../../services/financial-ops.service';

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const SECTION_HEADERS: Record<string, string> = {
  RECEITA_BRUTA: 'RECEITA BRUTA',
  PASS_THROUGH: '(−) PASS-THROUGH (TERCEIROS)',
  DESPESA_OPERACIONAL: '(−) DESPESAS OPERACIONAIS',
  NAO_OPERACIONAL: '(±) NÃO-OPERACIONAL',
};

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
}

export function DrePage() {
  const { closingId } = useParams<{ closingId: string }>();
  const { data, isLoading } = useDre(closingId);
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);

  const dre = data?.data;

  async function handleExport(format: 'xlsx' | 'pdf') {
    if (!closingId) return;
    setExporting(format);
    try {
      await financialOpsService.downloadDre(closingId, format);
    } catch {
      toast.error('Falha ao exportar a DRE. Tente novamente.');
    } finally {
      setExporting(null);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!dre) {
    return (
      <EmptyState
        title="Fechamento não encontrado"
        body="A DRE só está disponível para meses com fechamento calculado."
      />
    );
  }

  const { closing } = dre;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <Link
            to="/financeiro?tab=fechamento"
            className="inline-flex items-center gap-1 text-xs text-ink-secondary hover:text-ink-primary"
          >
            <ArrowLeft size={14} /> Fechamento Mensal
          </Link>
          <h2 className="text-lg font-semibold text-ink flex items-center gap-2">
            DRE — {MONTH_NAMES[closing.month - 1]}/{closing.year}
            <Badge tone={closing.status === 'FINALIZED' ? 'accent' : 'warning'}>
              {closing.status === 'FINALIZED' ? 'Finalizado' : 'Rascunho'}
            </Badge>
          </h2>
          <p className="text-sm text-ink-secondary">
            {closing.company?.name} · Realizado (transações pagas)
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/financeiro/relatorio-contador/${closingId}`}
            className="inline-flex items-center justify-center gap-1 h-8 px-3 text-xs font-medium rounded-md border border-border-strong text-ink-primary hover:bg-subtle"
          >
            <ClipboardList size={14} /> Relatório do Contador
          </Link>
          <Button size="sm" variant="ghost" loading={exporting === 'xlsx'} onClick={() => handleExport('xlsx')}>
            <Download size={14} className="mr-1" /> Exportar Excel
          </Button>
          <Button size="sm" variant="ghost" loading={exporting === 'pdf'} onClick={() => handleExport('pdf')}>
            <Download size={14} className="mr-1" /> Exportar PDF
          </Button>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-subtle text-ink-secondary">
            <tr>
              <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-xs">Item</th>
              <th className="px-4 py-2 text-right font-medium uppercase tracking-wider text-xs">Valor</th>
            </tr>
          </thead>
          <tbody>
            {dre.sections.map((section: any) => (
              <Fragment key={section.key}>
                <tr className="border-t border-border bg-subtle/60">
                  <td colSpan={2} className="px-4 py-2 text-xs font-semibold text-ink-secondary uppercase tracking-wider">
                    {SECTION_HEADERS[section.key] ?? section.label}
                  </td>
                </tr>
                {section.lines.map((line: any) => (
                  <tr key={`${section.key}-${line.label}`} className="border-t border-border">
                    <td className="px-4 py-2 pl-8 text-ink">{line.label}</td>
                    <td className="px-4 py-2 text-right text-ink">{formatCurrency(line.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t border-border font-semibold">
                  <td className="px-4 py-2 text-ink">Subtotal</td>
                  <td className="px-4 py-2 text-right text-ink">{formatCurrency(section.subtotal)}</td>
                </tr>
              </Fragment>
            ))}
            <tr className="border-t-2 border-border bg-subtle/60 font-semibold">
              <td className="px-4 py-2.5 text-ink">RECEITA LÍQUIDA</td>
              <td className="px-4 py-2.5 text-right text-ink">{formatCurrency(dre.receitaLiquida)}</td>
            </tr>
            <tr className="border-t border-border bg-subtle font-bold">
              <td className="px-4 py-2.5 text-ink">RESULTADO</td>
              <td className={`px-4 py-2.5 text-right ${dre.resultado >= 0 ? 'text-accent' : 'text-danger'}`}>
                {formatCurrency(dre.resultado)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
